import { demanderAcces, listerApplications } from './applications.js';
import { creerCanal } from './canal.js';
import { activerSlot, lireSlotActif, oublierSlot, } from './onglet.js';
import { ErreurPortail, lireCorps, refusDe, transportBff, } from './transport.js';
/**
 * L'état servi par le BFF (#87), restreint aux valeurs du contrat widget.
 * Tout le reste — absence (BFF ancien), valeur inconnue — retombe sur 'live' :
 * une pierre tombale ne naît que d'une DÉCLARATION, pas d'un mapping hasardeux.
 */
function etatServi(servi) {
    return servi === 'expired' || servi === 'revoked' ? servi : 'live';
}
/** Une promesse née dans une génération révolue n'a plus le droit de peindre. */
function erreurPerimee() {
    return new ErreurPortail(0, 'generation_perimee', 'Cette requête est née avant le dernier changement de compte : sa réponse ne vaut plus.');
}
/** Vrai si l'échec est l'abandon demandé par le passage de génération (fetch rejeté par le signal). */
function estAbandon(erreur) {
    return erreur?.name === 'AbortError';
}
/**
 * Une cible de retour ne quitte jamais l'origine : chemin absolu seul —
 * `//hote` (protocole relatif) et les barres inversées sont refusés, sinon un
 * paramètre « retour » deviendrait une primitive de redirection ouverte.
 */
const FORME_RETOUR_LOCAL = /^\/(?!\/)[^\\\s]*$/;
export function creerRuntime(options) {
    const stockage = options.stockage ?? globalThis.sessionStorage;
    if (stockage === undefined || stockage === null) {
        // Sans stockage d'onglet, le modèle « une identité PAR onglet » ne tient
        // pas : mieux vaut refuser net que de retomber en silence sur une clé
        // partagée entre onglets.
        throw new ErreurPortail(0, 'stockage_indisponible', 'sessionStorage est indisponible ici.');
    }
    const transport = options.transport ?? transportBff(options.lireJetonCsrf, options.origineBff);
    const naviguer = options.naviguer ??
        ((url) => {
            // `location` n'existe pas hors d'un navigateur : la navigation est un
            // acte de page, l'absence de page doit être un refus, pas un TypeError.
            if (typeof globalThis.location?.assign !== 'function') {
                throw new ErreurPortail(0, 'navigation_indisponible', 'Aucune page où naviguer ici.');
            }
            globalThis.location.assign(url);
        });
    // Dernier listage — la matière de `currentSession()`. Un cache VOLONTAIREMENT
    // borné à son ticket : la fraîcheur se regagne par un relistage, pas par une
    // lecture qui téléphonerait à chaque appel.
    let derniereListe = [];
    // Le compteur qui juge toutes les promesses : une lecture née sous N ne peut
    // plus rien écrire dès qu'un switch a fait N+1. Le contrôleur coupe le
    // réseau ; le compteur juge ce qui aurait malgré tout répondu (K-18).
    let generation = 0;
    let controleur = new AbortController();
    // ─── Observation : diffusion locale + canal inter-onglets (E4-T3) ───────────
    //
    // Sans BroadcastChannel dans l'environnement, `canal` est null : les verbes
    // agissent et parlent localement, rien n'est propagé — l'absence de signal
    // est le seul repli qui ne puisse pas dégénérer en adoption.
    const canal = options.canal !== undefined
        ? options.canal
        : typeof globalThis.BroadcastChannel === 'function'
            ? creerCanal()
            : null;
    const auditeurs = new Set();
    /** Diffuse un événement aux abonnés DE CET ONGLET (les autres onglets passent par le canal). */
    function diffuser(evenement) {
        for (const auditeur of auditeurs) {
            // Un auditeur qui jette est un défaut du consommateur : il ne doit
            // casser ni les autres auditeurs ni le verbe qui a réussi — on le
            // rapporte, on continue.
            try {
                auditeur(evenement);
            }
            catch (erreur) {
                console.error('[identity-runtime] un auditeur a échoué', erreur);
            }
        }
    }
    /**
     * Réception d'un signal d'un AUTRE onglet (RFC §11) : on RELIT d'abord la
     * source, puis on annonce — jamais l'inverse, et JAMAIS d'adoption : la clé
     * d'onglet n'est touchée que pour être refermée quand le slot qui la portait
     * a disparu, confirmé par la relecture.
     */
    async function traiterSignal(message) {
        if (message.type === 'trousseau-invalide') {
            // Le conteneur entier ne vaut plus : rien à relire, il n'y a rien —
            // purge, et le consommateur mène à la connexion.
            oublierSlot(stockage);
            derniereListe = [];
            diffuser({ type: 'keyring-invalidated' });
            return;
        }
        const relue = await listageAutoritaire(generation, controleur.signal);
        diffuser({ type: 'keyring-changed' });
        if (message.type === 'slot-retire' || message.type === 'session-revoquee') {
            const actif = lireSlotActif(stockage);
            if (actif !== null &&
                actif === message.slotRef &&
                !relue.some((s) => s.slotRef === actif)) {
                // CET onglet utilisait le slot parti : le consommateur affichera
                // « session retirée de ce navigateur » — la bascule automatique vers
                // un autre slot est interdite (critère d'acceptation du ticket).
                oublierSlot(stockage);
                diffuser({
                    type: 'slot-lost',
                    slotRef: actif,
                    reason: message.type === 'session-revoquee' ? 'revoked' : 'removed',
                });
            }
        }
    }
    canal?.ecouter((message) => {
        traiterSignal(message).catch((erreur) => {
            if (erreur instanceof ErreurPortail && erreur.code === 'generation_perimee') {
                // La garde a fait son œuvre : un switch local a rendu ce relistage
                // caduc — le switch hydrate lui-même, rien à annoncer ici.
                return;
            }
            // Impossible de relire la source : on n'annonce RIEN — peindre sur du
            // non-relu serait peindre périmé. Le prochain signal réconciliera.
            console.error('[identity-runtime] signal reçu mais source illisible', erreur);
        });
    });
    async function listageAutoritaire(gen, signal) {
        let reponse;
        try {
            reponse = await transport.appel('GET', '/portail/trousseau', { signal });
        }
        catch (erreur) {
            if (estAbandon(erreur))
                throw erreurPerimee();
            throw erreur;
        }
        // La résolution peut avoir couru plus vite que l'abandon : la génération
        // tranche — cette réponse appartient au monde d'avant.
        if (gen !== generation)
            throw erreurPerimee();
        const corps = (await lireCorps(reponse));
        // Un trousseau ABSENT n'est pas une erreur : ce navigateur n'a aucun
        // compte — une liste vide est sa seule vérité honnête.
        if (reponse.status === 401) {
            const refus = refusDe(reponse, corps);
            if (refus.code === 'trousseau_absent') {
                derniereListe = [];
                return derniereListe;
            }
            throw refus;
        }
        if (!reponse.ok)
            throw refusDe(reponse, corps);
        const actif = lireSlotActif(stockage);
        const brut = Array.isArray(corps?.slots) ? corps.slots : [];
        const defaut = typeof corps?.defaut === 'string' ? corps.defaut : null;
        derniereListe = brut
            .filter((s) => typeof s?.slotRef === 'string')
            .map((s) => ({
            slotRef: s.slotRef,
            identityId: typeof s.identite === 'string' ? s.identite : '',
            displayName: typeof s.displayName === 'string' ? s.displayName : '',
            email: typeof s.email === 'string' ? s.email : '',
            isDefault: s.slotRef === defaut,
            usedInThisTab: s.slotRef === actif,
            // #87 — le BFF ne purge PLUS les tombés : il sert des pierres
            // tombales (badge « Expirée » + login pré-rempli). On épouse l'état
            // SERVI, restreint aux valeurs du contrat widget : la pierre tombale
            // n'existe que si elle est DITE (voir etatServi).
            // DIVERGENCE SANDBOX : à porter dans le sdks/identity-runtime de
            // référence via ticket, avec le BFF correspondant.
            state: etatServi(s.state),
            sessionPublicId: typeof s.sessionPublicId === 'string' ? s.sessionPublicId : null,
        }));
        return derniereListe;
    }
    /** Referme la sélection de cet onglet SI elle vise encore `slotRef` — une sélection faite entre-temps a le dernier mot. */
    function refermerSiActif(slotRef) {
        if (lireSlotActif(stockage) === slotRef)
            oublierSlot(stockage);
    }
    /** Un refus du BFF, lu puis transporté tel quel (`corps` déjà consommé ici). */
    async function leverSiRefus(reponse) {
        if (reponse.ok)
            return;
        throw refusDe(reponse, await lireCorps(reponse));
    }
    return {
        async listBrowserSessions() {
            return listageAutoritaire(generation, controleur.signal);
        },
        currentSession() {
            const actif = lireSlotActif(stockage);
            if (actif === null)
                return null;
            return derniereListe.find((s) => s.slotRef === actif) ?? null;
        },
        async useInThisTab(slotRef) {
            const precedent = lireSlotActif(stockage);
            // LE SWITCH — synchrone, avant tout await : la génération tourne, les
            // lectures du monde d'avant sont abandonnées, le cache purgé, la clé
            // écrite. Trois appels rapprochés A → B → C posent donc C (K-18).
            generation += 1;
            controleur.abort();
            controleur = new AbortController();
            const gen = generation;
            derniereListe = [];
            activerSlot(stockage, slotRef);
            let sessions;
            try {
                sessions = await listageAutoritaire(gen, controleur.signal);
            }
            catch (erreur) {
                // Une sélection jamais confirmée par le serveur ne peint rien : on
                // REVIENT à l'identité précédente (RFC §25). Sauf si un switch plus
                // récent est intervenu entre-temps — sa sélection a le dernier mot.
                if (gen === generation) {
                    if (precedent === null)
                        oublierSlot(stockage);
                    else
                        activerSlot(stockage, precedent);
                }
                throw erreur;
            }
            const choisie = sessions.find((s) => s.slotRef === slotRef);
            if (choisie === undefined) {
                // Le slot visé n'existe plus côté serveur : restaurer le précédent
                // s'il figure ENCORE dans la liste fraîche — sinon refermer la clé
                // plutôt que de retomber sur une identité qui a disparu, elle aussi.
                const retombe = precedent !== null && sessions.some((s) => s.slotRef === precedent)
                    ? precedent
                    : null;
                if (retombe === null)
                    oublierSlot(stockage);
                else
                    activerSlot(stockage, retombe);
                throw new ErreurPortail(404, 'slot_inconnu', 'Ce compte n’est plus dans ce navigateur.');
            }
            // Le switch est confirmé par la relecture : cet onglet l'annonce à ses
            // abonnés, et les AUTRES onglets n'en reçoivent qu'un signal — « relis
            // », jamais « adopte » (E4-T3).
            diffuser({ type: 'slot-selected', slotRef });
            canal?.emettre({ type: 'slot-utilise', slotRef });
            return choisie;
        },
        async setDefault(slotRef) {
            const reponse = await transport.appel('PUT', '/portail/trousseau/defaut', {
                corps: { slotRef },
            });
            await leverSiRefus(reponse);
            // Le 204 confirme EXACTEMENT ceci : le défaut est dorénavant `slotRef`.
            // Les drapeaux du cache se retouchent donc sans re-listage — c'est la
            // seule mutation qui ne purge pas : elle ne change ni l'appartenance ni
            // la sélection, et repartir à vide priverait `currentSession()` d'une
            // vérité encore exacte.
            derniereListe = derniereListe.map((s) => ({
                ...s,
                isDefault: s.slotRef === slotRef,
            }));
            diffuser({ type: 'keyring-changed' });
            canal?.emettre({ type: 'defaut-change' });
        },
        addAccount(optionsAjout) {
            let cible = '/connexion?ajout=1';
            if (optionsAjout?.returnTo !== undefined) {
                if (!FORME_RETOUR_LOCAL.test(optionsAjout.returnTo)) {
                    throw new ErreurPortail(0, 'retour_refuse', 'Le retour après ajout doit rester sur cette origine.');
                }
                cible += `&retour=${encodeURIComponent(optionsAjout.returnTo)}`;
            }
            naviguer(cible);
        },
        async forgetInThisBrowser(slotRef) {
            // Corps, jamais URL (addendum 4 §2) : une URL est journalisée par les
            // serveurs d'accès, les proxies et les répartiteurs — « slotRef jamais
            // journalisé » (§27) ne se tient que sur un canal qui ne l'est pas.
            // POST : le corps d'un DELETE n'a pas de sémantique définie (RFC 9110).
            const reponse = await transport.appel('POST', '/portail/trousseau/retirer', {
                corps: { slotRef },
            });
            await leverSiRefus(reponse);
            refermerSiActif(slotRef);
            // Le cache contient une entrée que le serveur vient de retirer — et le
            // `defaut` a pu bouger avec elle. Purge plutôt qu'une retouche à la
            // main : seul un listage frais dira qui reste et qui est l'élu.
            derniereListe = [];
            diffuser({ type: 'keyring-changed' });
            canal?.emettre({ type: 'slot-retire', slotRef });
        },
        async signOut(slotRef) {
            const reponse = await transport.appel('POST', '/portail/session/deconnexion', {
                corps: { slotRef, portee: 'session' },
            });
            await leverSiRefus(reponse);
            refermerSiActif(slotRef);
            derniereListe = [];
            diffuser({ type: 'keyring-changed' });
            canal?.emettre({ type: 'session-revoquee', slotRef });
        },
        async signOutEverywhere(slotRef) {
            const reponse = await transport.appel('POST', '/portail/session/deconnexion', {
                corps: { slotRef, portee: 'identite' },
            });
            await leverSiRefus(reponse);
            // Une identité n'a JAMAIS deux slots dans un trousseau (RFC §9 pas 5 :
            // un ajout REMPLACE) — vider la sélection si elle visait ce slot suffit.
            refermerSiActif(slotRef);
            derniereListe = [];
            diffuser({ type: 'keyring-changed' });
            canal?.emettre({ type: 'session-revoquee', slotRef });
        },
        async forgetThisBrowser() {
            const reponse = await transport.appel('DELETE', '/portail/trousseau');
            // Effacer un navigateur SANS trousseau : le but est atteint. On referme
            // quand même une éventuelle sélection locale — sans trousseau derrière,
            // elle ne vise rien (seul ce refus-là est absorbé, et il le dit).
            if (reponse.status === 401) {
                const refus = refusDe(reponse, await lireCorps(reponse));
                if (refus.code !== 'trousseau_absent')
                    throw refus;
            }
            else {
                await leverSiRefus(reponse);
            }
            oublierSlot(stockage);
            derniereListe = [];
            // Le conteneur n'existe plus : l'événement n'est pas « le menu a changé »
            // mais « tout est à refaire » — localement et sur le fil. Les autres
            // onglets le recevront sans relecture : il n'y a plus rien à relire.
            diffuser({ type: 'keyring-invalidated' });
            canal?.emettre({ type: 'trousseau-invalide' });
        },
        async applications() {
            const gen = generation;
            let entrees;
            try {
                entrees = await listerApplications(transport, stockage, controleur.signal);
            }
            catch (erreur) {
                // Même jugement que le listage du trousseau : un abandon est le fait
                // du switch, et une réponse résolue sous l'ancienne génération peint
                // le rapport d'accès de l'identité d'AVANT — `generation_perimee`.
                if (estAbandon(erreur))
                    throw erreurPerimee();
                throw erreur;
            }
            if (gen !== generation)
                throw erreurPerimee();
            return entrees;
        },
        async requestApplicationAccess(clientId) {
            // Une MUTATION — comme les retraits et déconnexions : une demande
            // d'accès engagée doit se terminer, elle n'est pas abandonnée à un
            // switch (la garde de génération ne couvre que les lectures).
            return demanderAcces(transport, stockage, clientId);
        },
        subscribe(auditeur) {
            auditeurs.add(auditeur);
            return () => void auditeurs.delete(auditeur);
        },
    };
}
//# sourceMappingURL=runtime.js.map